import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Users, Plus, UserPlus, Trash2, X, Palette, Share2 } from 'lucide-react';

export default function TeamsPage() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [brandKits, setBrandKits] = useState([]);

  const load = async () => {
    try {
      const [t, bk] = await Promise.all([api.listTeams(), api.listBrandKits()]);
      setTeams(t);
      setBrandKits(bk);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!teamName.trim()) return;
    try {
      await api.createTeam({ name: teamName });
      toast.success('Team created!');
      setShowCreate(false);
      setTeamName('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !showInvite) return;
    try {
      await api.inviteToTeam(showInvite, { email: inviteEmail, role: inviteRole });
      toast.success(`Invited ${inviteEmail}`);
      setShowInvite(null);
      setInviteEmail('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemove = async (teamId, email) => {
    if (!window.confirm(`Remove ${email} from team?`)) return;
    try {
      await api.removeTeamMember(teamId, email);
      toast.success('Member removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleShareKit = async (teamId, kitId) => {
    try {
      await api.shareKitWithTeam(teamId, kitId);
      toast.success('Brand kit shared with team');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="teams-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Teams</h1>
          <p className="text-white/40 mt-1">Collaborate with your team, share brand kits</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm flex items-center gap-2" data-testid="create-team-btn">
          <Plus className="w-4 h-4" /> New Team
        </button>
      </div>

      {teams.length === 0 ? (
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
          <Users className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 mb-4">No teams yet. Create one to start collaborating.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm" data-testid="empty-create-team-btn">Create Team</button>
        </div>
      ) : (
        <div className="space-y-4">
          {teams.map(team => (
            <div key={team.id} className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 card-hover" data-testid={`team-${team.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">{team.name}</h3>
                  <p className="text-xs text-white/30 font-mono">{team.members?.length || 0} members · Created {team.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowInvite(team.id)} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid={`invite-btn-${team.id}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Invite
                  </button>
                  <button onClick={() => setSelectedTeam(selectedTeam === team.id ? null : team.id)} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5" data-testid={`share-kit-btn-${team.id}`}>
                    <Share2 className="w-3.5 h-3.5" /> Share Kit
                  </button>
                </div>
              </div>

              {/* Members */}
              <div className="space-y-2">
                {(team.members || []).map((m, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-white/[0.02] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-[#CDFE65]/20 flex items-center justify-center text-[#CDFE65] text-xs font-bold">
                        {(m.name || m.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm">{m.name || m.email}</p>
                        <p className="text-[10px] text-white/30 font-mono">{m.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-bold ${m.role === 'owner' ? 'bg-[#CDFE65]/10 text-[#CDFE65]' : m.role === 'admin' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' : 'bg-white/5 text-white/40'}`}>{m.role}</span>
                      {m.role !== 'owner' && (
                        <button onClick={() => handleRemove(team.id, m.email)} className="text-white/20 hover:text-red-400 transition-colors" data-testid={`remove-member-${m.email}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Shared Brand Kits */}
              {team.shared_brand_kits?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-xs font-mono uppercase tracking-wider text-white/30 mb-2">Shared Brand Kits</p>
                  <div className="flex flex-wrap gap-2">
                    {team.shared_brand_kits.map(kid => {
                      const kit = brandKits.find(bk => bk.id === kid);
                      return kit ? (
                        <span key={kid} className="text-xs px-2 py-1 bg-[#7C3AED]/10 text-[#7C3AED] rounded flex items-center gap-1">
                          <Palette className="w-3 h-3" /> {kit.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Share Kit Dropdown */}
              {selectedTeam === team.id && brandKits.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-xs font-mono uppercase tracking-wider text-white/30 mb-2">Share a Brand Kit</p>
                  <div className="flex flex-wrap gap-2">
                    {brandKits.map(bk => (
                      <button key={bk.id} onClick={() => handleShareKit(team.id, bk.id)} className="text-xs px-3 py-1.5 bg-white/5 hover:bg-[#CDFE65]/10 hover:text-[#CDFE65] rounded transition-colors" data-testid={`share-kit-${bk.id}`}>
                        {bk.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-team-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-sm mx-4 animate-fade-in-up opacity-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Create Team</h2>
              <button onClick={() => setShowCreate(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Team Name</label>
                <input value={teamName} onChange={e => setTeamName(e.target.value)} className="forge-input w-full" placeholder="My Creative Team" data-testid="team-name-input" />
              </div>
              <button onClick={handleCreate} className="btn-primary w-full text-sm" data-testid="create-team-submit">Create Team</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="invite-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-sm mx-4 animate-fade-in-up opacity-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Invite Member</h2>
              <button onClick={() => setShowInvite(null)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Email</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="forge-input w-full" placeholder="teammate@company.com" data-testid="invite-email-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Role</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="forge-input w-full" data-testid="invite-role-select">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <button onClick={handleInvite} className="btn-primary w-full text-sm" data-testid="invite-submit">Send Invite</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
